import type { SkuViewedPayload } from './Shibuya';

export type Action = 'confirm';
export type InteractionType = 'press';

export interface Impression extends Omit<ImpressionPayload, 'type'> {
  frame: Frame;
  impression_id?: string;
  navigation_id: string;
  parent_impression_id?: string;
}

export type ImpressionPayload =
  | {
      type: 'page_impression';
      entity_name: string;
    }
  | {
      type: 'product_impression';
      destination_url: string;
      element_position: number;
      entity_name?: string;
      marketplace_institution_id?: string | null;
      marketplace_offer_id?: string | null;
      marketplace_product_id?: string | null;
      marketplace_product_type: string;
      monetizable: Required<SkuViewedPayload>['monetizable'];
      section_name: string;
      section_position: number;
    }
  | {
      type: 'user_navigated';
      google_analytics_client_id?: string;
      initially_visible: boolean;
      // navigation_id: string
      pixel_ratio: number;
      url: string;
      viewport_height: number;
      viewport_width: number;
      // web_user_agent: string
      web_referrer?: string;
    }
  | {
      type: 'marketplace_offers_impression';
      entity_name: string;
      section_name: string;
      section_position: number;
      element_position: number | null;
      additional_attributes: {
        marketplace_offer_ids?: string[];
        marketplace_offers_count?: number;
        marketplace_product_type: string;
      };
    }
  | {
      type: 'content_impression';
      content_id: number;
      content_id_type: string;
      content_source: string;
    }
  | {
      type: 'registration_cta_impression';
      entity_name: string;
      section_name: string;
      section_position: number;
      element_position: number | null;
      additional_attributes: {
        target_flow: string;
        cta_mode: string;
        cta_label: string;
        cta_format: string;
      };
    }
  | {
      type: 'element_impression';
      entity_name: string;
      section_name?: string;
      section_position?: number;
    }
  | {
      type: 'recommended_credit_score_impression';
      entity_name: string;
      additional_attributes: {
        recommended_score: string;
      };
    }
  | {
      type: 'form_impression';
      form_name: string;
      entity_name?: string;
      section_name?: string;
      section_position?: number;
    }
  | {
      type: 'approval_odds_element_impression';
      entity_name: string;
      additional_attributes: {
        approval_odds: ApprovalOddsValue;
      };
    }
  | {
      type: 'cc_navigation_card_category';
      entity_name: string;
      section_name: string;
      section_position?: number;
      additional_attributes: {
        category_name: string;
      };
    }
  | {
      type: 'content_element_impression';
      content_id: number;
      content_id_type: string;
      content_source: string;
      entity_name: string;
      section_name: string;
    }
  | {
      type: 'bcc_page_impression';
      entity_name: string;
      display_mode: string;
      additional_attributes: {
        serp_category: string;
      };
    };

export type SpecificImpressionType<TType extends ImpressionPayload['type']> = Extract<
  ImpressionPayload,
  { type: TType }
>;

export type ElementInteractedPayload = {
  action: Action;
  interaction_type: InteractionType;
  section_name: string;
};

export interface Frame {
  deployable_environment?: string;
  deployable_name: string;
  deployable_version: string;
  device_id: string;
  event_occurred_msec_ago: number;
  event_seqno: number;
}

export enum LinkTypeCategory {
  APPLY_LINK = 'hyperlink',
  APPLY_NOW_BUTTON = 'cta',
  INSTITUTION_IMAGE = 'institutionImage',
  OFFER_LINK = 'cta',
  OFFER_NAME = 'productName',
  PRODUCT_IMAGE = 'productImage',
  PRODUCT_NAME = 'productName',
  RATES_AND_FEES_TEXT = 'cta',
  RECAP = 'Recap',
}

export type LinkType = keyof typeof LinkTypeCategory;

export enum FinishType {
  AfterLeadPartnerWebsite = 'after_lead_partner_website',
  ClaimPrequalOffer = 'claim_prequal_offer',
  ExternalApplication = 'external_application',
  ExternalContent = 'external_content',
  InternalContent = 'internal_content',
  LeadInputFlow = 'lead_inputflow',
  UnknownBug = 'unknown_bug',
}

export enum ApprovalOddsValue {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  POOR = 'poor',
  GOOD = 'good',
  EXCELLENT = 'excellent',
}

export interface AnalyticsMarketplacePayload {
  destination_url: string;
  element_position?: number;
  marketplace_institution_id?: string | null;
  marketplace_offer_id?: string | null;
  marketplace_product_id?: string | null;
  marketplace_product_type: string;
  monetizable: string;
  section_name?: string;
  section_position?: number;
}

export type TooltipActionType = 'open' | 'close';

export interface ImpressionOptions {
  navigationId: string;
  parentImpressionId: string;
  impressionPageUrl: string;
}

export interface CtaAnalytics {
  href: string;
  finishType?: FinishType;
  hasPrequalified?: boolean;
  includeClickHeaderParams?: boolean;
  isTrackOnly?: boolean;
  type?: LinkType;
  /** Typically the same as type, however sometimes the type and link type need to be different */
  linkType?: LinkType;
  monetizable?: SkuViewedPayload['monetizable'];
  /** The position of the product within the section, typically the index */
  productPosition?: number;
  /** The name of the credit card, such as "Chase-Sapphire-Preferred" */
  productSlug?: string;
  /** Details about where the apply link is located within the page */
  section?: {
    name?: string;
    position?: number;
    category?: string;
  };
  /**
   * Fields shared by product_impression and shopping_finished events,
   * sent directly into the payload of both
   */
  marketplacePayload?: AnalyticsMarketplacePayload;
  entityId?: string;
  /** The position of the product within the section, typically the index */
  impressionPosition?: number;
}
